import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Building2, Search, Save, RefreshCcw, ChevronDown, ChevronUp } from "lucide-react";
import LayoutWrapper from "../../components/LayoutWrapper";

const BASE_URL = "https://nissi-india.com"; // change if needed

const GoodsReceiptNoteAddListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // ✅ IMPORTANT: Inventory resolver uses finYearId = tbl_InfraConProjectsConnections.fld_id
  const finYearId = localStorage.getItem("finYearId") || ""; // e.g. 1014
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";

  const screenBadge = useMemo(() => "INFRA SCREEN : INVENTORY - GRN ADD LISTING", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  // UI state
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // meta/defaults
  const [defaultFromDate, setDefaultFromDate] = useState("");
  const [defaultToDate, setDefaultToDate] = useState("");

  // filters
  const [rad, setRad] = useState(branchType === "HO" ? "HO" : "BRANCH");
  const radChecked = rad === "HO" ? "H" : "B";

  const [supplierId, setSupplierId] = useState("Select");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [projectId, setProjectId] = useState("Select");
  const [projectCodeId, setProjectCodeId] = useState("Select");
  const [storeId, setStoreId] = useState("Select");
  const [poNo, setPoNo] = useState("");

  // dropdown data
  const [suppliers, setSuppliers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stores, setStores] = useState([]);

  // list
  const [rows, setRows] = useState([]);

  // ✅ selection MUST be declared before using it in dangerBtn
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const dangerBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (selectedIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700");

  const showingFrom = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, totalCount);

  // ---------------- validations ----------------
  const validateDates = () => {
    if (fromDate && toDate && toDate < fromDate) {
      toast.warn("Please select From Date less than or equal to To Date");
      return false;
    }
    const todayISO = new Date().toISOString().slice(0, 10);
    if (fromDate && fromDate > todayISO) {
      toast.warn("Select From Date lesser than or equal to current date");
      return false;
    }
    if (toDate && toDate > todayISO) {
      toast.warn("Select To Date lesser than or equal to current date");
      return false;
    }
    return true;
  };

  const mustHaveSupplier = () => {
    if (!supplierId || supplierId === "Select") {
      toast.warn("Please select supplier");
      return false;
    }
    return true;
  };

  const mustHaveFinYearId = () => {
    const id = Number(finYearId);
    if (!id || !Number.isFinite(id)) {
      toast.warn("Please select Financial Year.");
      return false;
    }
    return true;
  };

  // ---------------- API calls ----------------
  const fetchMeta = async () => {
    if (!branchId || !branchType) {
      toast.error("Missing branchId / branchType in localStorage");
      return;
    }
    if (!mustHaveFinYearId()) return;

    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-add-listing/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType,
        },
      });

      if (res?.data?.success) {
        setDefaultFromDate(res.data.defaultFromDate || "");
        setDefaultToDate(res.data.defaultToDate || "");
        setFromDate(res.data.defaultFromDate || "");
        setToDate(res.data.defaultToDate || "");
      } else {
        toast.error(res?.data?.message || "Failed to load meta");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta error");
    }
  };

  const fetchSuppliers = async () => {
    if (!branchId || !branchType) return;
    if (!mustHaveFinYearId()) return;

    try {
      const useMainBranch = branchType === "HO" && rad === "HO" ? "1" : "0";

      const res = await axios.get(`${BASE_URL}/api/inventory/grn-add-listing/suppliers`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType,
          useMainBranch,
        },
      });

      if (res?.data?.success) {
        setSuppliers(res.data.suppliers || []);
      } else {
        toast.error(res?.data?.message || "Failed to load suppliers");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Supplier load error");
    }
  };

  const fetchSupplierDetails = async (suppId) => {
    if (!mustHaveFinYearId()) return;

    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-add-listing/supplier-details`, {
        params: {
          finYearId: Number(finYearId),
          supplierId: Number(suppId),
          branchId: Number(branchId),
        },
      });

      if (res?.data?.success) {
        setProjects(res.data.projects || []);
        setStores(res.data.stores || []);

        setProjectId("Select");
        setProjectCodeId("Select");
        setStoreId("Select");
      } else {
        toast.error(res?.data?.message || "Failed to load supplier details");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Supplier detail error");
    }
  };

  const fetchList = async (p = page) => {
    if (!branchId || !branchType) {
      toast.error("Missing branchId / branchType");
      return;
    }
    if (!mustHaveFinYearId()) return;
    if (!mustHaveSupplier()) return;
    if (!validateDates()) return;

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/inventory/grn-add-listing/purchase-orders`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType,
          radChecked,
          supplierId: Number(supplierId),

          poNo: poNo.trim(),
          fromDate: fromDate || "",
          toDate: toDate || "",
          storeId: storeId !== "Select" ? Number(storeId) : 0,
          projectId: projectId !== "Select" ? Number(projectId) : 0,

          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotalCount(res.data.totalCount || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);

        setSelectedIds([]);
        setSelectAll(false);
      } else {
        toast.error(res?.data?.message || "Search failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Search error");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- selection ----------------
  const toggleSelect = (purchaseOrderId) => {
    setSelectedIds((prev) =>
      prev.includes(purchaseOrderId) ? prev.filter((x) => x !== purchaseOrderId) : [...prev, purchaseOrderId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }
    setSelectedIds(rows.map((r) => r.fld_PurchaseOrderid));
    setSelectAll(true);
  };

  // ---------------- actions ----------------
  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleRefresh = async () => {
    setSupplierId("Select");
    setProjects([]);
    setStores([]);
    setProjectId("Select");
    setProjectCodeId("Select");
    setStoreId("Select");
    setPoNo("");

    setFromDate(defaultFromDate || "");
    setToDate(defaultToDate || "");

    setRows([]);
    setSelectedIds([]);
    setSelectAll(false);
    setPage(1);
    setTotalCount(0);
    setTotalPages(1);

    await fetchSuppliers();
    toast.info("Reset done");
  };

  const handleSave = async () => {
    if (!mustHaveFinYearId()) return;
    if (!mustHaveSupplier()) return;

    if (selectedIds.length === 0) {
      toast.warn("Please select any One");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(
        `${BASE_URL}/api/inventory/grn-add-listing/save-selection`,
        {
          supplierId: Number(supplierId),
          grnType: rad === "HO" ? "HO" : "BRANCH",
          selectedPurchaseOrderIds: selectedIds,
        },
        { params: { finYearId: Number(finYearId) } }
      );

    if (res?.data?.success) {
sessionStorage.setItem("grnSelectionPayload", JSON.stringify(res.data.payload));
  toast.success("Saved selection");
  const redirectTo = res.data.redirectTo;
  if (redirectTo) window.location.href = `/Inventory/${redirectTo}`;
} else {
        toast.error(res?.data?.message || "Save failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save error");
    } finally {
      setLoading(false);
    }
  };

  const handleProjectCodeChange = (val) => {
    setProjectCodeId(val);
    setProjectId(val);
  };

  const handleProjectNameChange = (val) => {
    setProjectId(val);
    setProjectCodeId(val);
  };

  // ---------------- lifecycle ----------------
  useEffect(() => {
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rad]);

  useEffect(() => {
    if (supplierId && supplierId !== "Select") fetchSupplierDetails(supplierId);
    else {
      setProjects([]);
      setStores([]);
      setProjectId("Select");
      setProjectCodeId("Select");
      setStoreId("Select");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  useEffect(() => {
    if (supplierId && supplierId !== "Select") fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <LayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">GRN ADD LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Purchase Orders for GRN
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Supplier / Date / Project / Store / PO No + Pagination + Select & Save
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {totalCount} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleRefresh} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Reset
              </button>

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button onClick={handleSave} disabled={loading || selectedIds.length === 0} className={dangerBtn}>
                <Save className="w-4 h-4" />
                Save Selection
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelBase}>Branch</label>
                  <div className="mt-2 flex items-center gap-6">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="branch"
                        checked={rad === "HO"}
                        onChange={() => setRad("HO")}
                        disabled={branchType === "HO"}
                      />
                      <span className={isDark ? "text-gray-200" : "text-gray-700"}>H.O.</span>
                    </label>

                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="branch"
                        checked={rad === "BRANCH"}
                        onChange={() => setRad("BRANCH")}
                      />
                      <span className={isDark ? "text-gray-200" : "text-gray-700"}>BRANCH</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className={labelBase}>
                    Supplier <span className="text-red-500">*</span>
                  </label>
                  <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {suppliers.map((s) => (
                      <option key={s.supplierId} value={s.supplierId}>
                        {s.supplierName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>From Date</label>
                  <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={inputBase} />
                </div>

                <div>
                  <label className={labelBase}>To Date</label>
                  <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={inputBase} />
                </div>

                <div>
                  <label className={labelBase}>Project Code</label>
                  <select
                    value={projectCodeId}
                    onChange={(e) => handleProjectCodeChange(e.target.value)}
                    className={inputBase}
                    disabled={projects.length === 0}
                  >
                    <option value="Select">Select</option>
                    {projects.map((p) => (
                      <option key={p.projectId} value={p.projectId}>
                        {p.projectCode}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Project Name</label>
                  <select
                    value={projectId}
                    onChange={(e) => handleProjectNameChange(e.target.value)}
                    className={inputBase}
                    disabled={projects.length === 0}
                  >
                    <option value="Select">Select</option>
                    {projects.map((p) => (
                      <option key={p.projectId} value={p.projectId}>
                        {p.projectName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Purchase Order No</label>
                  <input value={poNo} onChange={(e) => setPoNo(e.target.value)} placeholder="Type PO No..." className={inputBase} />
                </div>

                <div>
                  <label className={labelBase}>Store</label>
                  <select value={storeId} onChange={(e) => setStoreId(e.target.value)} className={inputBase} disabled={stores.length === 0}>
                    <option value="Select">Select</option>
                    {stores.map((st) => (
                      <option key={st.storeId} value={st.storeId}>
                        {st.storeName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-center">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </form>
          )}

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">Slno</th>
                    <th className="px-4 py-4 text-left font-bold">Purchase Order No.</th>
                    <th className="px-4 py-4 text-left font-bold">Date</th>
                    <th className="px-4 py-4 text-left font-bold">Supplier</th>
                    <th className="px-4 py-4 text-left font-bold">Due Date</th>
                    <th className="px-4 py-4 text-left font-bold">Status</th>
                    <th className="px-4 py-4 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        Select All
                        <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 7 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={r.fld_PurchaseOrderid}
                        className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                      >
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_PONo}
                        </td>
                        <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_date}
                        </td>
                        <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_suppliername}
                        </td>
                        <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_duedate}
                        </td>
                        <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.status}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(r.fld_PurchaseOrderid)}
                            onChange={() => toggleSelect(r.fld_PurchaseOrderid)}
                            className="w-4 h-4"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{totalCount}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {totalPages > 1 && (
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        page === i + 1
                          ? `${primaryBtnClass} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}

              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh List
              </button>
            </div>
          </div>
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default GoodsReceiptNoteAddListing;